import { useMutation } from '@tanstack/react-query';
import { useNotify, useRefresh, useTranslate } from 'ra-core';
import { updateOfferNegotiation } from '../../../dataProvider/dataProvider';
import { Offer } from '../types';
import BaseNegotiationModal from './BaseNegotiationModal';
import { Button } from '@/components/ui/button';
import TermsCheckbox from '../../../components/ui/TermsCheckbox';
import DefaultAlert from '../../../components/ui/DefaultAlert';
import { useWatch, useFormContext } from 'react-hook-form';
import MoneyInput from '../../../components/ui/MoneyInput';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

type Props = {
  offer: Offer;
  open: boolean;
  onClose: () => void;
};

function CounterOfferModal({ offer, open, onClose }: Props) {
  const refresh = useRefresh();
  const t = useTranslate();
  const notify = useNotify();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Record<string, any>) =>
      updateOfferNegotiation('publisher-counter-proposal', data),
    onSuccess: () => {
      void refresh();
      notify(
        <DefaultAlert
          severity="success"
          alertTitle={t('offer.counterModal.notification.success')}
        />
      );
      onClose();
    },
    onError: error =>
      notify(
        <DefaultAlert
          severity="error"
          alertTitle={t('offer.counterModal.notification.error')}
          alertDescription={error?.message}
        />
      ),
  });

  const handleSubmit = (formData: any) => {
    mutate({
      parent_id: offer.last_negotiation_id,
      budget_total: Number(formData.negotiation.budget_total),
      rate: Number(formData.negotiation.rate),
      action_note: formData.negotiation.action_note,
      accepted_terms: formData.negotiation.accepted_terms,
    });
  };

  return (
    <BaseNegotiationModal
      open={open}
      onClose={onClose}
      title={t('offer.counterModal.title')}
      onSubmit={handleSubmit}
      actions={<CounterFormActions isPending={isPending} />}
    >
      <p className="mb-4 text-foreground">
        {t('offer.counterModal.description')}
      </p>
      <div className="grid gap-4 md:grid-cols-[repeat(auto-fit,minmax(0,1fr))] mb-4">
        <MoneyInput
          source="negotiation.budget_total"
          defaultValue={offer.last_negotiation.budget_total}
          label={t('common.text.total_budget')}
          placeholder="e.g., 1200.00"
          fullWidth
          helperText=""
        />
        {offer.last_negotiation.rate && (
          <MoneyInput
            source="negotiation.rate"
            defaultValue={offer.last_negotiation.rate}
            label="Rate"
            placeholder="e.g., 1.25"
            fullWidth
            step="0.01"
            min={0}
          />
        )}
      </div>
      <ActionNoteField
        placeholder={t('offer.common.placeholder.message')}
        label={t('offer.common.label.message')}
      />
      <TermsCheckbox source="accepted_terms" />
    </BaseNegotiationModal>
  );
}

export default CounterOfferModal;

function ActionNoteField({
  label,
  placeholder,
}: {
  label: string;
  placeholder: string;
}) {
  const { register } = useFormContext();
  return (
    <div className="flex flex-col gap-1 mb-4">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <Textarea
        {...register('negotiation.action_note')}
        rows={3}
        placeholder={placeholder}
        variant="muted"
      />
    </div>
  );
}

const CounterFormActions = ({ isPending }: { isPending: boolean }) => {
  const t = useTranslate();
  const accepted_terms = useWatch({ name: 'accepted_terms' });

  return (
    <Button
      type="submit"
      variant="default"
      disabled={!accepted_terms || isPending}
      size="lg"
      className="w-full"
    >
      {t('offer.counterModal.button')}
    </Button>
  );
};
